import type {
	Balance,
	ChatMessage,
	Listing,
	Notification,
	Offering,
	Partner,
	Payout,
	Position,
} from "@/types/residual";

export const partner: Partner = {
	id: "p_1",
	slug: "horizon",
	name: "Horizon Estates",
	displayCurrency: "USD",
};

export const offerings: Offering[] = [
	{
		id: "o_villa_bali",
		partnerId: "p_1",
		kind: "asset",
		status: "sale",
		title: "Beachfront Villa, Uluwatu",
		subtitle: "8-bedroom rental villa with managed operations",
		heroUrl:
			"https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1200&q=80",
		location: "Bali, Indonesia",
		chain: "BSC",
		tokenSymbol: "VBALI",
		tokenSupply: 10000,
		tokenPrice: 250,
		tokenCurrency: "USDT",
		soldTokens: 6420,
		deadlineAt: new Date(Date.now() + 18 * 86400_000).toISOString(),
		fundingStrategy: "partial-ok",
		projectedYearlyYieldPct: 8.4,
		legalDocs: [
			{ label: "Asset prospectus", url: "#" },
			{ label: "SPV structure", url: "#" },
		],
		tags: ["Real estate", "Rental yield"],
		requiresAccredited: false,
	},
	{
		id: "o_apartment_dubai",
		partnerId: "p_1",
		kind: "asset",
		status: "active",
		title: "Marina Apartment 22A",
		subtitle: "Long-term lease secured with FAB Dubai",
		heroUrl:
			"https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=80",
		location: "Dubai, UAE",
		chain: "ETH",
		tokenSymbol: "DUB22A",
		tokenSupply: 5000,
		tokenPrice: 400,
		tokenCurrency: "USDT",
		soldTokens: 5000,
		deadlineAt: new Date(Date.now() - 60 * 86400_000).toISOString(),
		fundingStrategy: "refund-all",
		projectedYearlyYieldPct: 6.2,
		legalDocs: [{ label: "Lease agreement", url: "#" }],
		tags: ["Real estate", "Long lease"],
		requiresAccredited: false,
	},
	{
		id: "o_coffee_chain",
		partnerId: "p_1",
		kind: "revenue-share",
		status: "sale",
		title: "Drift Coffee — Lisbon expansion",
		subtitle: "Three new locations launching Q1; 24-month payout window",
		heroUrl:
			"https://images.unsplash.com/photo-1453614512568-c4024d13c247?w=1200&q=80",
		location: "Lisbon, Portugal",
		chain: "TON",
		tokenSymbol: "DRIFT",
		tokenSupply: 20000,
		tokenPrice: 50,
		tokenCurrency: "USDT",
		soldTokens: 11200,
		deadlineAt: new Date(Date.now() + 9 * 86400_000).toISOString(),
		fundingStrategy: "extend",
		projectedYearlyYieldPct: 14,
		revenueShare: {
			percent: 18,
			windowMonths: 24,
			payoutCadence: "monthly",
			payoutCapUsd: 1_800_000,
		},
		legalDocs: [{ label: "Revenue-share terms", url: "#" }],
		tags: ["Hospitality", "Revenue share"],
		requiresAccredited: false,
	},
	{
		id: "o_film_ip",
		partnerId: "p_1",
		kind: "revenue-share",
		status: "draft",
		title: "Independent feature: 'Tide & Embers'",
		subtitle: "Distribution rights — payout cap at 1.5×",
		heroUrl:
			"https://images.unsplash.com/photo-1489599651275-b1ac2c87c4be?w=1200&q=80",
		location: "Worldwide",
		chain: "ETH",
		tokenSymbol: "TIDEMB",
		tokenSupply: 8000,
		tokenPrice: 125,
		tokenCurrency: "USDT",
		soldTokens: 0,
		deadlineAt: new Date(Date.now() + 45 * 86400_000).toISOString(),
		fundingStrategy: "refund-all",
		revenueShare: {
			percent: 25,
			windowMonths: 60,
			payoutCadence: "quarterly",
		},
		legalDocs: [{ label: "Rights agreement (draft)", url: "#" }],
		tags: ["IP", "Revenue share"],
		requiresAccredited: true,
	},
];

export const positions: Position[] = [
	{
		id: "pos_1",
		offeringId: "o_villa_bali",
		tokens: 12,
		purchasedAt: new Date(Date.now() - 42 * 86400_000).toISOString(),
		avgUnitPriceUsd: 250,
		currentValueUsd: 3186,
		totalIncomeUsd: 142.3,
		certificateUrl: "#",
	},
	{
		id: "pos_2",
		offeringId: "o_apartment_dubai",
		tokens: 5,
		purchasedAt: new Date(Date.now() - 96 * 86400_000).toISOString(),
		avgUnitPriceUsd: 400,
		currentValueUsd: 2080,
		totalIncomeUsd: 64.5,
		certificateUrl: "#",
	},
	{
		id: "pos_3",
		offeringId: "o_coffee_chain",
		tokens: 40,
		purchasedAt: new Date(Date.now() - 14 * 86400_000).toISOString(),
		avgUnitPriceUsd: 50,
		currentValueUsd: 2050,
		totalIncomeUsd: 0,
	},
];

export const payouts: Payout[] = [
	{
		id: "py_1",
		offeringId: "o_villa_bali",
		amountUsd: 38.6,
		receivedAt: new Date(Date.now() - 5 * 86400_000).toISOString(),
		periodLabel: "Apr 2026",
		status: "credited",
	},
	{
		id: "py_2",
		offeringId: "o_apartment_dubai",
		amountUsd: 27.0,
		receivedAt: new Date(Date.now() - 12 * 86400_000).toISOString(),
		periodLabel: "Apr 2026",
		status: "withdrawn",
	},
	{
		id: "py_3",
		offeringId: "o_villa_bali",
		amountUsd: 41.2,
		receivedAt: new Date(Date.now() - 35 * 86400_000).toISOString(),
		periodLabel: "Mar 2026",
		status: "reinvested",
	},
];

export const listings: Listing[] = [
	{
		id: "l_1",
		offeringId: "o_apartment_dubai",
		sellerId: "u_42",
		sellerLabel: "@maritza",
		tokens: 8,
		tokensRemaining: 8,
		askingPriceUsd: 415,
		createdAt: new Date(Date.now() - 3 * 86400_000).toISOString(),
		status: "listed",
		openOffers: 2,
	},
	{
		id: "l_2",
		offeringId: "o_villa_bali",
		sellerId: "u_77",
		sellerLabel: "@kestrel",
		tokens: 50,
		tokensRemaining: 30,
		askingPriceUsd: 268,
		createdAt: new Date(Date.now() - 11 * 86400_000).toISOString(),
		status: "negotiating",
		openOffers: 1,
	},
	{
		id: "l_3",
		offeringId: "o_coffee_chain",
		sellerId: "u_91",
		sellerLabel: "@orlov",
		tokens: 100,
		tokensRemaining: 100,
		askingPriceUsd: 53,
		createdAt: new Date(Date.now() - 1 * 86400_000).toISOString(),
		status: "listed",
		openOffers: 0,
	},
];

export const balance: Balance = {
	platformUsd: 412.85,
	referralUsd: 78.0,
	wallet: {
		kind: "custodial",
		address: "0x9aE3c8f4B2a1F7d6E5B2c1A0d9E8F7c6B5a4d3c2",
		chain: "BSC",
	},
};

export const notifications: Notification[] = [
	{
		id: "n_1",
		kind: "income",
		title: "Income credited",
		body: "+$38.60 from Beachfront Villa, Uluwatu",
		at: new Date(Date.now() - 5 * 86400_000).toISOString(),
		read: false,
	},
	{
		id: "n_2",
		kind: "offer-received",
		title: "New offer on your listing",
		body: "@argo offered $410 / token for 30 of your VBALI",
		at: new Date(Date.now() - 1 * 86400_000).toISOString(),
		read: false,
	},
	{
		id: "n_3",
		kind: "referral",
		title: "Referral reward",
		body: "+$12.50 from @sora's first purchase",
		at: new Date(Date.now() - 8 * 86400_000).toISOString(),
		read: true,
	},
];

export const sampleChat = (listingId: string): ChatMessage[] => [
	{
		id: `${listingId}_m1`,
		authorId: "u_42",
		authorLabel: "@maritza",
		role: "seller",
		body: "Listed 8 tokens at $415 each. Open to a small discount on the full lot.",
		at: new Date(Date.now() - 86400_000 * 2).toISOString(),
	},
	{
		id: `${listingId}_m2`,
		authorId: "self",
		authorLabel: "You",
		role: "buyer",
		body: "Would $400 work for all 8?",
		at: new Date(Date.now() - 86400_000 * 1.5).toISOString(),
		offer: { tokens: 8, pricePerTokenUsd: 400 },
	},
	{
		id: `${listingId}_m3`,
		authorId: "u_42",
		authorLabel: "@maritza",
		role: "seller",
		body: "Can do $408 — meets in the middle.",
		at: new Date(Date.now() - 86400_000 * 0.8).toISOString(),
		offer: { tokens: 8, pricePerTokenUsd: 408 },
	},
];
